import { t } from '@/i18n';
import type {
  MinistriesType,
  MinistryFileType,
  MinistryType,
  SpecificMinistryType,
} from '../ministry';

type FormatsType =
  | {
      formatsType: 'formats';
      formats: MinistryFileType[];
      customFormats?: never;
      year: string;
    }
  | {
      formatsType: 'customFormats';
      formats?: never;
      year?: never;
      customFormats: (ministry: MinistryType) => MinistriesType['files'];
    };

type Params = {
  ministry: MinistryType;
} & FormatsType;

export const getSheetByMinistry = ({
  year,
  ministry,
  formatsType,
  formats,
  customFormats,
}: Params): MinistriesType => {
  return {
    key: ministry,
    name: MinistryNameModifier[ministry],
    files:
      formatsType === 'customFormats'
        ? customFormats(ministry)
        : formats.map((format) => {
            return {
              fileType: format,
              fileName: getFileNameByMinistry({
                year,
                format,
                ministry,
              }),
            };
          }),
  };
};

type MinistryByFileName = MinistryType | 'cabinet-office';

const getFileNameByMinistry = ({
  year,
  format,
  ministry,
}: {
  year: string;
  format: MinistryFileType;
  ministry: MinistryByFileName;
}): string => {
  return t('ps-sheet-name-by-ministry', {
    year,
    format: FormatNameModifier[format],
    ministry: MinistryNameByFileNameModifier[ministry],
  });
};

export const getSpecificMinistryFiles = ({
  year,
  formats,
  specificMinistry,
  ministry,
}: {
  specificMinistry: SpecificMinistryType;
  year: string;
  formats: MinistryFileType[];
  ministry: MinistryByFileName;
}): MinistriesType['files'] => {
  return formats.map((format) => {
    return {
      specificMinistry,
      fileType: format,
      fileName: getFileNameByMinistry({
        year,
        format,
        ministry,
      }),
    };
  });
};

const FormatNameModifier = {
  rsFormat1: t('ps-sheet-format1-name'),
  rsFormat2: t('ps-sheet-format2-name'),
  rsFormat3: t('ps-sheet-format3-name'),
  rsFormat4: t('ps-sheet-format4-name'),
  ks: t('ps-sheet-name-ks'),
  ksLocalGovernmentA: t('ps-sheet-name-local-government-basic-information'),
  ksLocalGovernmentB: t(
    'ps-sheet-name-local-government-income-and-expenditure',
  ),
  ksInvestment: t('ps-sheet-name-ks-investment'),
} as const satisfies Record<MinistryFileType, string>;

const MinistryNameModifier = {
  'cabinet-secretariat-cabinet-office': t(
    'ps-ministry-name-cabinet-secretariat-cabinet-office',
  ),
  'fair-trade-commission': t('ps-ministry-name-fair-trade-commission'),
  'national-police-agency': t('ps-ministry-name-national-police-agency'),
  'personal-information-protection-commission': t(
    'ps-ministry-name-personal-information-protection-commission',
  ),
  'casino-regulatory-commission': t(
    'ps-ministry-name-casino-regulatory-commission',
  ),
  'financial-services-agency': t('ps-ministry-name-financial-services-agency'),
  'consumer-affairs-agency': t('ps-ministry-name-consumer-affairs-agency'),
  'children-and-families-agency': t(
    'ps-ministry-name-children-and-families-agency',
  ),
  'digital-agency': t('ps-ministry-name-digital-agency'),
  'reconstruction-agency': t('ps-ministry-name-reconstruction-agency'),
  'ministry-of-internal-affairs-and-communications': t(
    'ps-ministry-name-ministry-of-internal-affairs-and-communications',
  ),
  'ministry-of-justice': t('ps-ministry-name-ministry-of-justice'),
  'ministry-of-foreign-affairs': t(
    'ps-ministry-name-ministry-of-foreign-affairs',
  ),
  'ministry-of-finance': t('ps-ministry-name-ministry-of-finance'),
  'ministry-of-education-culture-sports-science-and-technology': t(
    'ps-ministry-name-ministry-of-education-culture-sports-science-and-technology',
  ),
  'ministry-of-health-labour-and-welfare': t(
    'ps-ministry-name-ministry-of-health-labour-and-welfare',
  ),
  'ministry-of-agriculture-forestry-and-fisheries': t(
    'ps-ministry-name-ministry-of-agriculture-forestry-and-fisheries',
  ),
  'ministry-of-economy-trade-and-industry': t(
    'ps-ministry-name-ministry-of-economy-trade-and-industry',
  ),
  'ministry-of-land-infrastructure-and-transport': t(
    'ps-ministry-name-ministry-of-land-infrastructure-and-transport',
  ),
  'ministry-of-the-environment': t(
    'ps-ministry-name-ministry-of-the-environment',
  ),
  'nuclear-regulation-authority': t(
    'ps-ministry-name-nuclear-regulation-authority',
  ),
  'ministry-of-defense': t('ps-ministry-name-ministry-of-defense'),
} as const satisfies Record<MinistryType, string>;

const MinistryNameByFileNameModifier = {
  ...MinistryNameModifier,
  'cabinet-secretariat-cabinet-office': t(
    'ps-ministry-name-cabinet-secretariat',
  ),
  'ministry-of-defense': t('ps-ministry-name-ministry-of-defense-by-file-name'),
  'cabinet-office': t('ps-ministry-name-cabinet-office'),
} as const satisfies Record<MinistryByFileName, string>;
